/* eslint-disable react/no-array-index-key */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Print as PrintIcon } from '@mui/icons-material';
import { capitalize, Grid } from '@mui/material';

import { PRIMARY_GRAY } from 'commons/styles/colors';
import { formatCurrency } from 'commons/utils/formatCurrency';
import { formatDate } from 'commons/utils/formatDate';
import { deliveryNotePrintPDF } from 'commons/utils/printDeliveryNotePDF.util';
import {
  getDeliveryNoteDetailAction,
  printDeliveryNoteAction,
} from 'redux-stores/actions/deliveryNote.action';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';
import { DELIVERY_NOTE_ROUTES } from 'routes/constants';

import {
  DELIVERY_NOTE_STATUS_BADGE_COLORS,
  DELIVERY_NOTE_STATUS_DISPLAY,
  PRINTABLE_STATUSES,
} from '../dn-list/DNListPage.constants';
import { getDeliveryType } from '../utils';

import S from './DNDetailPage.styles';

const DNDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedDeliveryNote } = useSelector(
    (state: RootReducerInterface) => state.deliveryNote,
  );

  useEffect(() => {
    if (selectedDeliveryNote?.awb_number) {
      dispatch(getDeliveryNoteDetailAction(selectedDeliveryNote.awb_number));
    }
  }, [dispatch, selectedDeliveryNote?.awb_number]);

  const handlePrint = async (): Promise<void> => {
    if (!selectedDeliveryNote) return;
    await dispatch(printDeliveryNoteAction(selectedDeliveryNote.awb_number));
    await deliveryNotePrintPDF(selectedDeliveryNote);
  };

  if (!selectedDeliveryNote) {
    navigate(DELIVERY_NOTE_ROUTES.LIST, { replace: true });
    return null;
  }

  const dn = selectedDeliveryNote;
  const isPrintable = PRINTABLE_STATUSES.includes(dn.status);
  const statusColors = DELIVERY_NOTE_STATUS_BADGE_COLORS[dn.status] ?? {
    bg: '#E5E5E5',
    color: '#525252',
  };
  const statusText = DELIVERY_NOTE_STATUS_DISPLAY[dn.status] ?? dn.status;

  return (
    <S.PageWrapper>
      <S.CardTopRow>
        <S.StatusRow>
          <span>Status:</span>
          <S.StatusBadge $bg={statusColors.bg} $color={statusColors.color}>
            {statusText}
          </S.StatusBadge>
        </S.StatusRow>
        <S.PrintButton
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={!isPrintable}
        >
          Print
        </S.PrintButton>
      </S.CardTopRow>

      <S.InfoSection>
        <Grid container rowGap={2}>
          <Grid container item xs={6} md={3}>
            <Grid item xs={12}>
              <S.InfoLabel>AWB Number</S.InfoLabel>
            </Grid>
            <Grid item xs={12}>
              <S.InfoValue>{dn.awb_number}</S.InfoValue>
            </Grid>
          </Grid>
          <Grid item xs={6} md={3}>
            <Grid item xs={12}>
              <S.InfoLabel>Tanggal</S.InfoLabel>
            </Grid>
            <Grid item xs={12}>
              <S.InfoValue>
                {formatDate(dn.ordered_at, 'dd MMMM yyyy')}
              </S.InfoValue>
            </Grid>
          </Grid>
          <Grid item xs={6} md={3}>
            <Grid item xs={12}>
              <S.InfoLabel>Tanggal Jatuh Tempo</S.InfoLabel>
            </Grid>
            <Grid item xs={12}>
              <S.InfoValue>
                {formatDate(dn.due_date, 'dd MMMM yyyy')}
              </S.InfoValue>
            </Grid>
          </Grid>
          <Grid item xs={6} md={3}>
            <Grid item xs={12}>
              <S.InfoLabel>Delivery Type</S.InfoLabel>
            </Grid>
            <Grid item xs={12}>
              <S.InfoValue>
                {getDeliveryType(
                  dn.is_regular_delivery,
                  dn.is_coldchain_delivery,
                )}
              </S.InfoValue>
            </Grid>
          </Grid>
        </Grid>
      </S.InfoSection>

      <S.InfoSection>
        <Grid container rowGap={2}>
          <Grid item xs={6} md={3}>
            <Grid item xs={12}>
              <S.InfoLabel>Customer ID</S.InfoLabel>
            </Grid>
            <Grid item xs={12}>
              <S.InfoValue>{dn.pharmacy_id}</S.InfoValue>
            </Grid>
          </Grid>
          <Grid item xs={6} md={3}>
            <Grid item xs={12}>
              <S.InfoLabel>Nama Customer</S.InfoLabel>
            </Grid>
            <Grid item xs={12}>
              <S.InfoValue>{dn.pharmacy_name ?? '-'}</S.InfoValue>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid item xs={12}>
              <S.InfoLabel>Alamat Apotek</S.InfoLabel>
            </Grid>
            <Grid item xs={12}>
              {' '}
              <S.InfoValue>{dn.pharmacy_address ?? '-'}</S.InfoValue>
            </Grid>
          </Grid>
        </Grid>
      </S.InfoSection>

      <S.TableWrapper>
        <S.StyledTable>
          <thead>
            <tr>
              <S.TableHeaderCell>No</S.TableHeaderCell>
              <S.TableHeaderCell>PO Number</S.TableHeaderCell>
              <S.TableHeaderCell>No Invoice</S.TableHeaderCell>
              <S.TableHeaderCell>Status</S.TableHeaderCell>
              <S.TableHeaderCell>Total Koli</S.TableHeaderCell>
              <S.TableHeaderCell>Tipe Dokumen</S.TableHeaderCell>
              <S.TableHeaderCell>Amount</S.TableHeaderCell>
            </tr>
          </thead>
          <tbody>
            {(dn.invoices ?? []).map((invoice, idx) => (
              <tr key={`${invoice.invoice_number}-${idx}`}>
                <S.TableBodyCell>{idx + 1}</S.TableBodyCell>
                <S.TableBodyCell>{invoice.po_number}</S.TableBodyCell>
                <S.TableBodyCell>
                  {invoice.invoice_number?.replace(/ /g, '') ?? '-'}
                </S.TableBodyCell>
                <S.TableBodyCell>
                  {capitalize(invoice.picking_status)}
                </S.TableBodyCell>
                <S.TableBodyCell>{invoice.koli_count} Box</S.TableBodyCell>
                <S.TableBodyCell>
                  {invoice.is_digital_signed ? 'Elektronik' : 'Manual'}
                </S.TableBodyCell>
                <S.TableBodyCell>
                  {formatCurrency(invoice.invoiced_amount)}
                </S.TableBodyCell>
              </tr>
            ))}
            {(dn.invoices ?? []).length === 0 && (
              <tr>
                <S.TableBodyCell
                  colSpan={8}
                  style={{ textAlign: 'center', color: PRIMARY_GRAY }}
                >
                  No invoices
                </S.TableBodyCell>
              </tr>
            )}
          </tbody>
        </S.StyledTable>
      </S.TableWrapper>
    </S.PageWrapper>
  );
};

export default DNDetailPage;
